import React, { useState } from "react";
import styled from "styled-components";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { FaTimes, FaClipboardCheck, FaExclamationCircle, FaSearch, FaFilePdf } from "react-icons/fa";

const guinda = "#611232";

export default function ConsultaTramite({ isOpen, onClose }) {
  const [formData, setFormData] = useState({ folio: "" });
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  React.useEffect(() => {
    if (!isOpen) {
      setFormData({ folio: "" });
      setResultado(null);
      setError("");
      setLoading(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const consultar = async (e) => {
    e.preventDefault();
    if (!formData.folio) return;

    setLoading(true);
    setError("");
    setResultado(null);

    try {
      const q = query(
        collection(db, "tramites"),
        where("folio", "==", formData.folio.trim())
      );
      const snap = await getDocs(q);

      if (!snap.empty) {
        let docData = snap.docs[0].data();
        
        // Buscar si existe un expediente en PDF asociado a este folio
        try {
          const qExp = query(collection(db, "expedientes"), where("folio", "==", formData.folio.trim()));
          const snapExp = await getDocs(qExp);
          if (!snapExp.empty) {
            docData.pdfUrl = snapExp.docs[0].data().pdfUrl;
          }
        } catch (e) {
          console.error("No se pudo obtener el expediente", e);
        }

        setResultado(docData);
      } else {
        setError("No se encontró ningún trámite con ese número de folio.");
      }
    } catch (err) {
      setError("Error al conectar con el servidor. Intente más tarde.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <FaTimes />
        </CloseButton>

        <IconContainer>
          <IconWrapper>
            <FaClipboardCheck />
          </IconWrapper>
        </IconContainer>

        {!resultado ? (
          <>
            <Titulo>Consultar Estado de Trámite</Titulo>
            <Subtitulo>Ingresa tu número de folio para conocer el estatus legal de tu caso.</Subtitulo>
            <Form onSubmit={consultar}>
              <SearchInputContainer>
                <SearchIcon><FaSearch /></SearchIcon>
                <Input
                  placeholder="Ej: EXP0000000AP"
                  value={formData.folio}
                  onChange={(e) => setFormData({ ...formData, folio: e.target.value })}
                  required
                />
              </SearchInputContainer>

              {error && (
                <ErrorMessage>
                  <FaExclamationCircle /> {error}
                </ErrorMessage>
              )}

              <SubmitButton type="submit" disabled={loading}>
                {loading ? "Consultando..." : "Consultar Ahora"}
              </SubmitButton>
            </Form>
          </>
        ) : (
          <ResultViewContainer>
            <HeaderResult>
              <div>
                <HolaText>Hola, {resultado.nombre ? resultado.nombre.split(' ')[0] : "Ciudadano"}</HolaText>
                <EstadoTitle>ESTADO DEL TRAMITE</EstadoTitle>
              </div>
              <ActivoBadge>ACTIVO</ActivoBadge>
            </HeaderResult>

            <ResultCard>
              <StatusBadge status={resultado.estatus}>{resultado.estatus}</StatusBadge>
              <ResultTitle>DETALLES DEL TRAMITE</ResultTitle>
              
              <ResultRow style={{ marginBottom: '1.5rem', alignItems: 'center' }}>
                <ResultLabel>FOLIO:</ResultLabel>
                <ResultValue>{resultado.folio}</ResultValue>
              </ResultRow>
              
              <ResultRow style={{ flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
                <ResultLabel>RESOLUCIÓN:</ResultLabel>
                <ResultValue style={{ fontWeight: 'normal', lineHeight: '1.4' }}>{resultado.resolucion || "Trámite en proceso de revisión."}</ResultValue>
              </ResultRow>

              {resultado.requerimiento && (
                <ResultRow style={{ flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
                  <ResultLabel>REQUERIMIENTO:</ResultLabel>
                  <ResultValue style={{ fontWeight: 'normal', lineHeight: '1.4' }}>{resultado.requerimiento}</ResultValue>
                </ResultRow>
              )}

              {resultado.cantidad && (
                <CantidadBox>
                  Total a entregar: {resultado.cantidad}
                </CantidadBox>
              )}

              <ResultRow style={{ flexDirection: 'column', gap: '0.3rem', marginTop: '1rem' }}>
                <ResultLabel>ULTIMA ACTUALIZACIÓN:</ResultLabel>
                <ResultValue>{resultado.fecha}</ResultValue>
              </ResultRow>
              
              {resultado.pdfUrl && (
                <PdfButton type="button" onClick={() => window.open(resultado.pdfUrl, "_blank")}>
                  <FaFilePdf size={20} /> Descargar Expediente PDF
                </PdfButton>
              )}
            </ResultCard>

            <SubmitButton type="button" onClick={() => setResultado(null)} style={{ marginTop: '2rem' }}>
              Consultar otro folio
            </SubmitButton>
          </ResultViewContainer>
        )}
      </ModalContent>
    </Overlay>
  );
}

// Estilos
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 1.5rem;
`;

const ModalContent = styled.div`
  background: white;
  width: 100%;
  max-width: 450px;
  padding: 3rem 2.5rem;
  border-radius: 2rem;
  position: relative;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #ccc;
  cursor: pointer;
  transition: color 0.2s;
  &:hover { color: #333; }
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
`;

const IconWrapper = styled.div`
  width: 65px;
  height: 65px;
  background: ${guinda};
  color: white;
  border-radius: 20px;
  display: grid;
  place-items: center;
  font-size: 1.8rem;
`;

const Titulo = styled.h2`
  margin: 0 0 1rem;
  font-weight: 900;
  color: #1a1a1a;
  font-size: 1.5rem;
  text-align: center;
`;

const Subtitulo = styled.p`
  text-align: center;
  color: #666;
  font-size: 0.95rem;
  margin-top: 0;
  margin-bottom: 2rem;
  line-height: 1.5;
`;

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SearchInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 1.2rem;
  color: #999;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem 1.2rem 1rem 3.5rem;
  border: 1.5px solid #e0e0e0;
  border-radius: 15px;
  font-size: 1.05rem;
  font-weight: 600;
  font-family: inherit;
  transition: all 0.2s;
  &:focus {
    outline: none;
    border-color: ${guinda};
    box-shadow: 0 0 0 4px rgba(97, 18, 50, 0.1);
  }
  &::placeholder {
    color: #b3b3b3;
    font-weight: 400;
  }
`;

const SubmitButton = styled.button`
  background: ${guinda};
  color: white;
  border: none;
  border-radius: 15px;
  padding: 1.2rem;
  font-size: 1.1rem;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.3s;
  width: 100%;
  box-shadow: 0 10px 20px rgba(97, 18, 50, 0.15);

  &:hover {
    background: #4a0d26;
    transform: translateY(-2px);
    box-shadow: 0 15px 30px rgba(97, 18, 50, 0.25);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #d93025;
  background: #fdf2f2;
  padding: 0.8rem;
  border-radius: 10px;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  line-height: 1.4;
`;

const ResultViewContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const HeaderResult = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 1.5rem;
`;

const HolaText = styled.div`
  color: ${guinda};
  font-weight: 800;
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
`;

const EstadoTitle = styled.h2`
  margin: 0;
  font-weight: 900;
  color: #000;
  font-size: 1.4rem;
  text-transform: uppercase;
`;

const ActivoBadge = styled.div`
  background: ${guinda};
  color: white;
  padding: 0.4rem 1rem;
  border-radius: 12px;
  font-weight: 800;
  font-size: 0.85rem;
`;

const ResultCard = styled.div`
  background: #e8e8e8;
  border-radius: 20px;
  padding: 1.5rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StatusBadge = styled.div`
  background: ${props => props.status === 'Resuelto' ? '#e6f4ea' : '#fff3e0'};
  color: ${props => props.status === 'Resuelto' ? '#137333' : '#b75a00'};
  padding: 0.5rem 1rem;
  border-radius: 100px;
  font-size: 0.9rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
`;

const ResultTitle = styled.h3`
  margin: 0 0 1.5rem 0;
  color: #000;
  font-size: 1rem;
  font-weight: 900;
`;

const ResultRow = styled.div`
  display: flex;
  width: 100%;
  gap: 0.5rem;
`;

const ResultLabel = styled.span`
  color: #777;
  font-size: 0.85rem;
`;

const ResultValue = styled.span`
  color: #000;
  font-weight: 800;
  font-size: 0.95rem;
`;

const CantidadBox = styled.div`
  background: ${guinda};
  color: white;
  width: 100%;
  padding: 1rem;
  border-radius: 12px;
  text-align: center;
  font-weight: 700;
  margin-bottom: 1.5rem;
`;

const PdfButton = styled.button`
  background: #333;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 0.8rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 800;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-top: 2rem;
  transition: all 0.3s;
  width: 100%;
  justify-content: center;

  &:hover {
    background: #1a1a1a;
  }
`;
